import { useRef, useState } from 'react';
import { QuestionProps } from '@g17eco/types/questionInterfaces';
import { GlossaryText } from '@g17eco/molecules/glossary-text/GlossaryText';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { InstructionLink } from './InstructionLink';
import { getInstructionLabel } from '@utils/universalTracker';
import { useTextCollapse } from '@hooks/useTextCollapse';
import { ReadMoreButton } from './ReadMoreButton';

interface Props extends Pick<QuestionProps, 'utr' | 'glossaryState' | 'alternativeCode'> {
  selectedLanguage?: string;
  startAltTextAnimation: boolean;
  instructionsLinkSize?: string;
}

const SingleInstructions = (props: Props & { isHidden: boolean; children: JSX.Element }) => {
  const { utr, glossaryState, alternativeCode, isHidden, instructionsLinkSize } = props;
  const instructionRef = useRef<HTMLDivElement | null>(null);

  const { isTruncated, height, className } = useTextCollapse({
    elementRef: instructionRef,
    isHidden,
  });

  if (!utr) {
    return <LoadingPlaceholder height={40} />;
  }

  const instructionsText = getInstructionLabel(alternativeCode);
  const instructionsLink = utr.getInstructionsLink(alternativeCode);
  const instructions: string = utr.getInstructions(alternativeCode);

  return (
    <>
      <div ref={instructionRef} className={`instruction-text ${className}`} style={{ height }}>
        <GlossaryText text={instructions} glossary={glossaryState} />
        <InstructionLink link={instructionsLink} label={instructionsText} size={instructionsLinkSize} />
      </div>
      {isTruncated ? props.children : null}
    </>
  );
};

export const Instructions = (props: Props) => {
  const { utr, alternativeCode, selectedLanguage, startAltTextAnimation } = props;

  const [isHidden, setIsHidden] = useState(true);
  const toggleHiding = () => setIsHidden((prev) => !prev);

  const showInstructionLink = !isHidden && utr?.getInstructionsLink(alternativeCode);
  const button = (
    <ReadMoreButton isHidden={isHidden} onToggle={toggleHiding} className={showInstructionLink ? 'mt-2' : ''} />
  );

  return (
    <div className='animated-container'>
      <div className={`text-animated ${startAltTextAnimation ? 'disappear-right' : ''}`}>
        <SingleInstructions {...props} alternativeCode={alternativeCode} isHidden={isHidden}>
          {button}
        </SingleInstructions>
      </div>
      <div className={`text-animated ${startAltTextAnimation ? '' : 'disappear-left'}`}>
        <SingleInstructions {...props} alternativeCode={selectedLanguage ?? alternativeCode} isHidden={isHidden}>
          {button}
        </SingleInstructions>
      </div>
    </div>
  );
};
